/**
 * A registrar-supplied roster file, read into the typed import's rows (ADR 0036).
 *
 * The registrar brings a CSV or a JSON file; the import takes keyed rows. This
 * module is the mapping between them and nothing more: it derives each row's
 * `import_key` from the facts the registrar wrote, so a re-exported roster
 * resolves to the same rows, and it names what it could not read instead of
 * guessing. The server validates every row again, per row, on the dry run.
 *
 * CSV columns (header row, any order, extra columns refused):
 *
 *   kind               student | enrollment
 *   student_id         an existing IMAM SID, or blank to issue a new one
 *   given_name, family_name
 *   delivery_email     the contact the invitation will use; never an identity
 *   program_key        the program's code, optional
 *   cohort_key         the cohort's name, optional
 *   admitted_year      four digits, optional (cohort year for a new SID)
 *   student_key        enrollment rows: the student row's key (its SID, or
 *                      the given name, family name and contact joined by |)
 *   course_code, term_name
 *
 * The key of a student row is its SID when it has one; otherwise the three
 * facts that name a new person, joined by `|`, folded to lower case. The key
 * of an enrollment row is its student key, course code and term name joined
 * the same way. Keys are the registrar's facts, so the registrar can read
 * them on the receipts and a changed name is a changed row.
 *
 * ONE ROW PER ENROLLMENT. A CSV whose header has no kind column is read as a
 * roster export instead: each line one learner in one section, its columns
 * mapped onto ROSTER_FIELDS (lib/source-columns.ts says how a column is
 * suggested; the registrar sees and can change every one). Each line gives a
 * student row, keyed exactly as above, and an enrollment row for its course
 * code and term name; a line with neither is a student only. Lines repeating a
 * student's values collapse into one row; the same key with different values
 * is a problem naming the lines. Each derived row's source_ref records the
 * lines it came from.
 */
import type { ImportRowInput } from "@/lib/api/imports";
import { collapse, lineOf, missingFields, sentenceLabels, tableOf, valuesOf, type ColumnMap, type SourceField, type SourceTable } from "@/lib/source-columns";

export const ROSTER_COLUMNS = [
  "kind",
  "student_id",
  "given_name",
  "family_name",
  "delivery_email",
  "program_key",
  "cohort_key",
  "admitted_year",
  "student_key",
  "course_code",
  "term_name",
] as const;

type RosterColumn = (typeof ROSTER_COLUMNS)[number];

export interface RosterReading {
  rows: ImportRowInput[];
  /** Lines the reader could not turn into a row, each with its line number. */
  problems: string[];
}

function fold(value: string): string {
  return value.trim().replace(/\s+/g, " ").toLowerCase();
}

export function studentKey(fields: { student_id?: string; given_name?: string; family_name?: string; delivery_email?: string }): string {
  const sid = fields.student_id?.trim();
  if (sid) return sid;
  return [fields.given_name ?? "", fields.family_name ?? "", fields.delivery_email ?? ""].map(fold).join("|");
}

export function enrollmentKey(fields: { student_key: string; course_code: string; term_name: string }): string {
  return [fields.student_key.trim(), fold(fields.course_code), fold(fields.term_name)].join("|");
}

/** RFC 4180 enough for a registrar export: quoted fields, doubled quotes, CRLF. */
export function parseCsv(text: string): string[][] {
  const records: string[][] = [];
  let record: string[] = [];
  let field = "";
  let quoted = false;
  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    if (quoted) {
      if (char === '"') {
        if (text[index + 1] === '"') {
          field += '"';
          index += 1;
        } else {
          quoted = false;
        }
      } else {
        field += char;
      }
      continue;
    }
    if (char === '"') {
      quoted = true;
    } else if (char === ",") {
      record.push(field);
      field = "";
    } else if (char === "\n" || char === "\r") {
      if (char === "\r" && text[index + 1] === "\n") index += 1;
      record.push(field);
      records.push(record);
      record = [];
      field = "";
    } else {
      field += char;
    }
  }
  if (field !== "" || record.length > 0) {
    record.push(field);
    records.push(record);
  }
  return records.filter((row) => row.some((cell) => cell.trim() !== ""));
}

function rowFromFields(fields: Partial<Record<RosterColumn, string>>, line: number): ImportRowInput | string {
  const kind = fold(fields.kind ?? "");
  const present = (name: RosterColumn) => (fields[name] ?? "").trim();
  if (kind === "student") {
    const data: Record<string, unknown> = {
      given_name: present("given_name"),
      family_name: present("family_name"),
    };
    if (present("student_id")) data.student_id = present("student_id");
    if (present("delivery_email")) data.delivery_email = present("delivery_email");
    if (present("program_key")) data.program_key = present("program_key");
    if (present("cohort_key")) data.cohort_key = present("cohort_key");
    if (present("admitted_year")) {
      const year = Number.parseInt(present("admitted_year"), 10);
      if (!Number.isInteger(year)) return `line ${line}: admitted_year ${present("admitted_year")} is not a year`;
      data.admitted_year = year;
    }
    for (const stray of ["student_key", "course_code", "term_name"] as const) {
      if (present(stray)) return `line ${line}: a student row does not carry ${stray}`;
    }
    return {
      entity_kind: "student",
      import_key: studentKey(data as { student_id?: string; given_name: string; family_name: string; delivery_email?: string }),
      source_ref: { line },
      data,
    };
  }
  if (kind === "enrollment") {
    const key = present("student_key");
    const course = present("course_code");
    const term = present("term_name");
    if (!key || !course || !term) {
      return `line ${line}: an enrollment row needs student_key, course_code and term_name`;
    }
    for (const stray of ["student_id", "given_name", "family_name", "delivery_email", "program_key", "cohort_key", "admitted_year"] as const) {
      if (present(stray)) return `line ${line}: an enrollment row does not carry ${stray}`;
    }
    return {
      entity_kind: "enrollment",
      import_key: enrollmentKey({ student_key: key, course_code: course, term_name: term }),
      source_ref: { line },
      data: { student_key: key, course_code: course, term_name: term },
    };
  }
  return `line ${line}: kind must be student or enrollment, not ${JSON.stringify(fields.kind ?? "")}`;
}

export function readRosterCsv(text: string): RosterReading {
  const records = parseCsv(text);
  if (records.length === 0) return { rows: [], problems: ["the file is empty"] };
  const header = records[0].map((cell) => fold(cell));
  const unknown = header.filter((name) => !(ROSTER_COLUMNS as readonly string[]).includes(name));
  if (unknown.length > 0) {
    return { rows: [], problems: [`unknown column${unknown.length === 1 ? "" : "s"}: ${unknown.join(", ")}`] };
  }
  if (!header.includes("kind")) return { rows: [], problems: ["the header has no kind column"] };
  const rows: ImportRowInput[] = [];
  const problems: string[] = [];
  records.slice(1).forEach((record, offset) => {
    const line = offset + 2;
    const fields: Partial<Record<RosterColumn, string>> = {};
    header.forEach((name, column) => {
      fields[name as RosterColumn] = record[column] ?? "";
    });
    const row = rowFromFields(fields, line);
    if (typeof row === "string") problems.push(row);
    else rows.push(row);
  });
  return { rows, problems };
}

function isRowEnvelope(value: unknown): value is { rows: unknown[] } {
  return typeof value === "object" && value !== null && Array.isArray((value as { rows?: unknown }).rows);
}

export function readRosterJson(text: string): RosterReading {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { rows: [], problems: ["the file is not valid JSON"] };
  }
  const list = Array.isArray(parsed) ? parsed : isRowEnvelope(parsed) ? parsed.rows : null;
  if (!list) return { rows: [], problems: ["the file is neither a list of rows nor an object with a rows list"] };
  const rows: ImportRowInput[] = [];
  const problems: string[] = [];
  list.forEach((item, index) => {
    if (typeof item !== "object" || item === null) {
      problems.push(`row ${index + 1} is not an object`);
      return;
    }
    const candidate = item as Partial<ImportRowInput>;
    if (candidate.entity_kind !== "student" && candidate.entity_kind !== "enrollment") {
      problems.push(`row ${index + 1}: entity_kind must be student or enrollment`);
      return;
    }
    if (typeof candidate.import_key !== "string" || !candidate.import_key.trim()) {
      problems.push(`row ${index + 1}: import_key is required`);
      return;
    }
    rows.push({
      entity_kind: candidate.entity_kind,
      import_key: candidate.import_key,
      source_ref: candidate.source_ref ?? { row: index + 1 },
      data: candidate.data ?? {},
    });
  });
  return { rows, problems };
}

/** IMAM's own roster template: its header names a kind column. */
export function isRosterTemplate(header: readonly string[]): boolean {
  return header.some((cell) => fold(cell) === "kind");
}

/** A roster file as read: the template's rows, or an export's table waiting for its columns. */
export interface RosterSource extends RosterReading { table?: SourceTable }

export function readRosterSource(name: string, text: string): RosterSource {
  if (!name.toLowerCase().endsWith(".csv")) return readRosterFile(name, text);
  const records = parseCsv(text);
  if (records.length === 0) return { rows: [], problems: ["the file is empty"] };
  if (isRosterTemplate(records[0])) return readRosterCsv(text);
  return { rows: [], problems: [], table: tableOf(records) };
}

export type RosterField = "student_id" | "given_name" | "family_name" | "delivery_email" | "program_key" | "cohort_key" | "admitted_year" | "course_code" | "term_name";

/** The fields a one-row-per-enrollment roster export fills, with the only aliases that prefill them. */
export const ROSTER_FIELDS: readonly SourceField<RosterField>[] = [
  { id: "student_id", label: "IMAM SID", required: false, aliases: ["sid", "student id", "student number"] },
  { id: "given_name", label: "Given name", required: true, aliases: ["first name"] },
  { id: "family_name", label: "Family name", required: true, aliases: ["last name", "surname"] },
  { id: "delivery_email", label: "Delivery email", required: false, aliases: ["email", "e-mail", "email address"] },
  { id: "program_key", label: "Program", required: false, aliases: ["program key", "programme"] },
  { id: "cohort_key", label: "Cohort", required: false, aliases: ["cohort key"] },
  { id: "admitted_year", label: "Admitted year", required: false, aliases: ["admission year", "year admitted"] },
  { id: "course_code", label: "Course code", required: true, aliases: ["code", "course"] },
  { id: "term_name", label: "Term name", required: true, aliases: ["term", "semester"] },
];

const ROSTER_LABELS = sentenceLabels(ROSTER_FIELDS);

/** A roster export's rows, derived as the module header says. The map must give every required field a column. */
export function deriveRoster(table: SourceTable, map: ColumnMap): RosterReading {
  const students = collapse("student", ROSTER_LABELS), enrollments = collapse("enrollment", ROSTER_LABELS);
  const enrollmentOf = new Map<string, { student_key: string; course_code: string; term_name: string }>();
  const problems: string[] = [];
  table.records.forEach((record, offset) => {
    const line = lineOf(offset);
    const v = valuesOf(ROSTER_FIELDS, map, record);
    const blank = (["given_name", "family_name"] as const).filter((f) => !v[f]).map((f) => ROSTER_LABELS[f]);
    if (blank.length) { problems.push(`line ${line}: no ${blank.join(", ")}`); return; }
    if (Boolean(v.course_code) !== Boolean(v.term_name)) { problems.push(`line ${line}: an enrollment needs both its course code and term name`); return; }
    if (v.admitted_year && !Number.isInteger(Number.parseInt(v.admitted_year, 10))) { problems.push(`line ${line}: admitted year ${v.admitted_year} is not a year`); return; }
    const key = studentKey(v);
    students.add(key, { student_id: v.student_id, given_name: v.given_name, family_name: v.family_name, delivery_email: v.delivery_email,
      program_key: v.program_key, cohort_key: v.cohort_key, admitted_year: v.admitted_year }, line);
    if (!v.course_code) return;
    const enrollment = enrollmentKey({ student_key: key, course_code: v.course_code, term_name: v.term_name });
    if (!enrollmentOf.has(enrollment)) enrollmentOf.set(enrollment, { student_key: key, course_code: v.course_code, term_name: v.term_name });
    enrollments.add(enrollment, { course_code: v.course_code, term_name: v.term_name }, line);
  });
  const rows: ImportRowInput[] = [
    ...students.entries().map(({ key, values, lines }) => ({ entity_kind: "student" as const, import_key: key, source_ref: { lines },
      data: Object.fromEntries(Object.entries({ ...values, admitted_year: values.admitted_year && Number.parseInt(values.admitted_year, 10) })
        .filter(([field, value]) => value !== "" || field === "given_name" || field === "family_name")) })),
    ...enrollments.entries().map(({ key, lines }) => ({ entity_kind: "enrollment" as const, import_key: key, source_ref: { lines }, data: { ...enrollmentOf.get(key)! } })),
  ];
  return { rows, problems: [...problems, ...students.problems(), ...enrollments.problems()] };
}

/** A roster export's rows under a map: none until every required field has a column. */
export function readMappedRoster(table: SourceTable, map: ColumnMap): RosterReading {
  return missingFields(ROSTER_FIELDS, map).length ? { rows: [], problems: [] } : deriveRoster(table, map);
}

/** The roster export example: a continuing student by SID and a new student, one line per enrollment. */
export function rosterExportTemplateCsv(): string {
  const lines = [
    ROSTER_FIELDS.map((f) => f.label),
    ["26001", "Yusuf", "Haddad", "", "", "", "", "ETH-201", "Fall 2026"],
    ["", "Amina", "Rahman", "amina@example.org", "", "", "2026", "ETH-201", "Fall 2026"],
    ["", "Amina", "Rahman", "amina@example.org", "", "", "2026", "FIQ-110", "Fall 2026"],
  ];
  const cell = (value: string) => /[",\r\n]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value;
  return `${lines.map((line) => line.map(cell).join(",")).join("\r\n")}\r\n`;
}

/** Read a roster file by its name's extension: `.csv` by the column contract, `.json` as typed rows. */
export function readRosterFile(name: string, text: string): RosterReading {
  const lower = name.toLowerCase();
  if (lower.endsWith(".csv")) return readRosterCsv(text);
  if (lower.endsWith(".json")) return readRosterJson(text);
  return { rows: [], problems: [`${name} is neither a .csv nor a .json file`] };
}

/** A roster file's rows, summarised the way the registrar counts them. */
export function summariseRoster(rows: ImportRowInput[]): { students: number; newStudents: number; enrollments: number } {
  const students = rows.filter((row) => row.entity_kind === "student");
  return {
    students: students.length,
    newStudents: students.filter((row) => !(row.data as { student_id?: unknown } | undefined)?.student_id).length,
    enrollments: rows.filter((row) => row.entity_kind === "enrollment").length,
  };
}

/** The template's example: a continuing student by SID, a new student, and the new student's enrollment. */
export const ROSTER_TEMPLATE_ROWS: Array<Partial<Record<RosterColumn, string>>> = [
  { kind: "student", student_id: "26001", given_name: "Yusuf", family_name: "Haddad" },
  { kind: "student", given_name: "Amina", family_name: "Rahman", delivery_email: "amina@example.org", admitted_year: "2026" },
  { kind: "enrollment", student_key: "amina|rahman|amina@example.org", course_code: "ETH-201", term_name: "Fall 2026" },
  { kind: "enrollment", student_key: "26001", course_code: "ETH-201", term_name: "Fall 2026" },
];

/** The downloadable template: the header and the example rows. */
export function rosterTemplateCsv(): string {
  const cell = (value: string) => /[",\r\n]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value;
  const lines = [ROSTER_COLUMNS.join(","), ...ROSTER_TEMPLATE_ROWS.map((row) => ROSTER_COLUMNS.map((c) => cell(row[c] ?? "")).join(","))];
  return `${lines.join("\r\n")}\r\n`;
}
